'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { clearLogoutQueries } from '../providers/queryClient';
import { logoutUser } from '../utils/logout';
import { useAuth0Logout } from './useAuth0Logout';
import { useAuth0SessionLogout } from './useAuth0SessionLogout';
import { useDecisions } from './useDecisions';

export type UseLogoutOptions = {
    callback?: () => void;
    /** Redirect URL used by the legacy logout path when Auth0 is disabled. */
    redirectUrl?: string;
    skipRedirect?: boolean;
    forceRedirect?: boolean;
};

/**
 * Unified logout hook that delegates to Auth0 or the legacy logout path
 * depending on the `auth0` feature flag.
 */
export function useLogout() {
    const isAuth0Enabled = useDecisions('auth0');
    const auth0Logout = useAuth0Logout();
    const auth0SessionLogout = useAuth0SessionLogout();
    const queryClient = useQueryClient();

    return useCallback(
        async ({ callback, redirectUrl = '/sign-in', skipRedirect, forceRedirect }: UseLogoutOptions = {}) => {
            if (isAuth0Enabled) {
                if (skipRedirect) {
                    await auth0SessionLogout({ callback, skipRedirect, redirectUrl });
                } else {
                    await auth0Logout({ callback });
                }
            } else {
                await logoutUser({
                    redirectUrl,
                    skipRedirect,
                    forceRedirect,
                    callback: () => {
                        callback?.();
                        clearLogoutQueries(queryClient);
                    },
                });
            }
        },
        [isAuth0Enabled, auth0Logout, auth0SessionLogout, queryClient]
    );
}
